import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface ListPaneProps {
  /** Content for the header area (typically DataControls or custom header) */
  header?: ReactNode;

  /** Main content area (typically List with ListItems) */
  children: ReactNode;

  /** Optional footer content */
  footer?: ReactNode;

  /** Width of the pane */
  width?: "sm" | "md" | "lg" | "xl" | "2xl" | "auto";

  /** Theming */
  tone?: "neutral" | "subtle" | "brand";
  elevation?: "none" | "sm" | "md";
  showDivider?: boolean;

  /** Accessibility */
  "aria-label"?: string;
  "aria-labelledby"?: string;

  className?: string;
  style?: React.CSSProperties;
}

/**
 * ListPane
 *
 * A simple container component for list-driven UIs.
 * Provides a structured layout with optional header and footer,
 * suitable for wrapping DataControls, List, and ListItem components.
 *
 * Example usage:
 * ```tsx
 * <ListPane
 *   width="md"
 *   header={<DataControls search={...} filters={...} sort={...} />}
 *   footer={<Button>Add Site</Button>}
 * >
 *   <List items={sites} renderItem={...} />
 * </ListPane>
 * ```
 */
export function ListPane({
  header,
  children,
  footer,
  width = "md",
  tone = "neutral",
  elevation = "sm",
  showDivider = true,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  className = "",
  style,
}: ListPaneProps) {
  // Width styles
  const widthStyles = {
    sm: "w-64",
    md: "w-80",
    lg: "w-96",
    xl: "w-[28rem]",
    "2xl": "w-[32rem]",
    auto: "w-auto",
  };

  // Tone styles
  const toneStyles = {
    neutral: "bg-background-surface",
    subtle: "bg-background-subtle",
    brand: "bg-background-brand",
  };

  // Elevation styles
  const elevationStyles = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
  };

  const composedClassName = twMerge(
    "flex flex-col h-full",
    widthStyles[width],
    toneStyles[tone],
    elevationStyles[elevation],
    showDivider && "border-r border-border-muted",
    className
  );

  return (
    <div
      className={composedClassName}
      style={style}
      role="complementary"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {/* Sticky header */}
      {header && (
        <div className="flex-shrink-0 sticky top-0 z-10 bg-inherit border-b border-border-muted">{header}</div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">{children}</div>

      {/* Optional footer */}
      {footer && <div className="flex-shrink-0 border-t border-border-muted bg-inherit">{footer}</div>}
    </div>
  );
}
